import type {
  CharacterInput,
  CharacterLogInput,
  CharacterLogUpdateInput,
  CharacterQuickAddInput,
  CharacterUpdateInput,
  CharacterWithLogs,
  PlayerInput,
  PlayerUpdateInput,
  PlayerWithCharacters,
  RosterSummary,
  RosterSyncStatus,
} from '@moussenaires/shared';

import { api } from './client.js';

export const rosterApi = {
  summary: () => api.get<RosterSummary>('/roster/summary'),
  players: () => api.get<PlayerWithCharacters[]>('/roster/players'),
  player: (id: number) => api.get<PlayerWithCharacters>(`/roster/players/${id}`),
  createPlayer: (input: PlayerInput) => api.post<PlayerWithCharacters>('/roster/players', input),
  updatePlayer: (id: number, input: PlayerUpdateInput) =>
    api.patch<PlayerWithCharacters>(`/roster/players/${id}`, input),
  deletePlayer: (id: number) => api.delete<void>(`/roster/players/${id}`),

  character: (id: number) => api.get<CharacterWithLogs>(`/roster/characters/${id}`),
  createCharacter: (playerId: number, input: CharacterInput) =>
    api.post<CharacterWithLogs>(`/roster/players/${playerId}/characters`, input),
  /** The usual way to add a character: name + realm, synced from the Armory right away. */
  quickAddCharacter: (playerId: number, input: CharacterQuickAddInput) =>
    api.post<CharacterWithLogs>(`/roster/players/${playerId}/characters/quick`, input),
  updateCharacter: (id: number, input: CharacterUpdateInput) =>
    api.patch<CharacterWithLogs>(`/roster/characters/${id}`, input),
  deleteCharacter: (id: number) => api.delete<void>(`/roster/characters/${id}`),
  /** Re-fetches from the Armory/Raider.IO/Warcraft Logs, bypassing the cache. */
  syncCharacter: (id: number) => api.post<CharacterWithLogs>(`/roster/characters/${id}/sync`),
  /**
   * Re-syncs every character at once — open to anyone, limited by a single
   * global 5-minute cooldown (not per-user), whose live state is also
   * pushed over the WebSocket as soon as this starts.
   */
  syncAll: () =>
    api.post<{ total: number; synced: number; cooldownUntil: string }>('/roster/sync-all'),
  /** The current cooldown, for a client loading the page mid-cooldown (the WebSocket only covers *changes* from here on). */
  syncStatus: () => api.get<RosterSyncStatus>('/roster/sync-status'),
  /** Attaches (or, with `null`, removes) a character's hero GIF/clip — an already-uploaded, unclaimed media asset. */
  setCharacterMedia: (id: number, mediaId: number | null) =>
    api.patch<CharacterWithLogs>(`/roster/characters/${id}/media`, { mediaId }),

  addLog: (characterId: number, input: CharacterLogInput) =>
    api.post(`/roster/characters/${characterId}/logs`, input),
  updateLog: (id: number, input: CharacterLogUpdateInput) => api.patch(`/roster/logs/${id}`, input),
  deleteLog: (id: number) => api.delete<void>(`/roster/logs/${id}`),
};
